import { getCurrentInstance, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { appStateKey, StartupState, _useAppState } from '@gat-ship/app-state'
import { AppError, ErrorCode, mapToAppError } from '@gat-ship/error-handling'
import {
  supportSessionContextKey,
  useSupportSession
} from '@gat-ship/support-session'
import { registerSupportAssignedRoute } from '@gat-ship/support'
import { userServiceKey, useUser } from '@gat-ship/user'
import { _useSignalR, signalRKey } from '@gat-ship/signalr'
import { _useFeatureFlag, featureFlagKey } from '@gat-ship/feature-flag'
import {
  _useGlobalSettings,
  globalSettingsKey
} from '@gat-ship/global-settings'
import { useAuth0 } from '@gat-ship/auth'
import { useDatabaseSessionStorage } from '@gat-ship/database'
import { useGsCloudBackend } from '@gat-ship/http-client'
import { userPreferencesInjectionKey } from '@gat-ship/vue-components'
import {
  type DatabaseAccessGrantedSignalRPayload,
  type DatabaseAccessRevokedSignalRPayload,
  signalREvent
} from '@gat-ship/types/signal-r-events'

/**
 * Owns the app's bootstrap promise-chain. Call from a component's
 * `<script setup>`, then invoke `initializeApp()` inside `onMounted()` so
 * `provideContexts()`'s synchronous prefix runs while the component
 * instance is still active (per §3 row 0 of the cluster-promotion plan).
 */
export function useSessionBootstrap() {
  const auth0 = useAuth0()
  const router = useRouter()
  const route = useRoute()
  const supportSessionContext = useSupportSession()
  const userContext = useUser()
  const appStateContext = _useAppState()
  const signalRContext = _useSignalR()
  const featureFlagContext = _useFeatureFlag()
  const globalSettingsContext = _useGlobalSettings()
  const databaseStorage = useDatabaseSessionStorage()
  const gsCloudBackend = useGsCloudBackend()

  async function isAuthenticated() {
    if (auth0.isAuthenticated.value) {
      return true
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (
          appStateContext.startupState.startupState.value.includes(
            StartupState.Initializing
          )
        ) {
          // Problems with auth0.
          reject(new Error('User profile loading timed out after 60 seconds'))
        }
      }, 60000)
      const unwatch = watch(
        auth0.isAuthenticated,
        (newValue) => {
          if (newValue) {
            clearTimeout(timeout)
            unwatch()
            resolve(true)
          }
        },
        { immediate: true, deep: true }
      )
    })
  }

  /**
   * Defensively re-provides all service contexts at the app level so
   * `app.config.globalProperties` consumers and detached render targets see
   * them. Idempotent — already-provided keys are skipped.
   *
   * Must run as the first synchronous statement of `initializeApp()` so
   * `getCurrentInstance()` still resolves to the calling component (the
   * `onMounted` callback's synchronous prefix qualifies).
   */
  function provideContexts() {
    const app = getCurrentInstance()?.appContext.app
    if (app) {
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          appStateKey
        )
      ) {
        app.provide(appStateKey, appStateContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          supportSessionContextKey
        )
      ) {
        app.provide(supportSessionContextKey, supportSessionContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          userServiceKey
        )
      ) {
        app.provide(userServiceKey, userContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(app._context.provides, signalRKey)
      ) {
        app.provide(signalRKey, signalRContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          featureFlagKey
        )
      ) {
        app.provide(featureFlagKey, featureFlagContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          globalSettingsKey
        )
      ) {
        app.provide(globalSettingsKey, globalSettingsContext)
      }
      // Bridge the lib's user preferences down to the minimal shape that
      // @gat-ship/vue-components primitives consume (the date inputs read
      // dateFormat, modal reads modalType). The getter form means each
      // consumer call inside computed() picks up live changes from
      // userContext.userPreferences without a re-provide.
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          userPreferencesInjectionKey
        )
      ) {
        app.provide(userPreferencesInjectionKey, () => {
          const prefs = userContext.userPreferences.getUserPreferences()
          return {
            dateFormat: prefs.dateFormat,
            modalType: prefs.modalType
          }
        })
      }
    }
  }

  /**
   * [§3 rows 9–11] — fetch granted databases, write storage, resolve
   * tenant id (support-session-aware).
   */
  async function resolveTenantId(): Promise<string> {
    try {
      // [§3 row 9]
      const result = await userContext._fetchGrantedDatabases()
      // [§3 row 10] — preserve today's non-empty guard
      if (Array.isArray(result.databases) && result.databases.length > 0) {
        databaseStorage.setDatabases(result.databases)
      }
      // [§3 row 11] — support-session-aware tenant resolution mirrors
      // today's `getTenantId()` body inside `useDatabaseSessionStorage`.
      if (supportSessionContext.isSupportSession.value) {
        databaseStorage.getDefaultSupportSessionDatabaseId()
      }
      const userDefaultDbId = await userContext.userState.global
        .getState('defaultDatabaseId')
        .catch(() => '')
      return databaseStorage.getSelectedDatabaseId(userDefaultDbId || undefined)
    } catch (err) {
      throw mapToAppError(err)
    }
  }

  /** [§3 rows 17–19] — feature-flag identity, flag init, global settings. */
  async function initializeFlagsAndSettings(args: {
    tenantId: string
    tenantName: string
    userEmail: string
    currentDatabase: typeof databaseStorage.databaseInfo.value.current
  }) {
    // [§3 row 17]
    const databaseId = args.currentDatabase?.id
    const databaseName = args.currentDatabase?.name
    const userId = userContext.id.value
    const database =
      databaseId && databaseName
        ? {
            key: databaseId,
            name: databaseName
          }
        : undefined

    // [§3 row 18]
    await featureFlagContext._initialize({
      user: {
        key: userId,
        name: args.userEmail
      },
      tenant: {
        key: args.tenantId,
        name: args.tenantName
      },
      database
    })
    featureFlagContext.setDisabledFlags(
      () =>
        userContext.userPreferences.getUserPreferences().disabledFeatureFlags ??
        []
    )
    // [§3 row 19]
    await globalSettingsContext._initialize({
      userId,
      databaseId
    })
  }

  /**
   * [Post-§3 addition] Gatship support users get the assigned-sessions
   * route registered at runtime — for everyone else the URL stays
   * unresolvable, as defense in depth on top of the role-gated sidebar
   * entry. Needs the user profile (row 6) to be loaded.
   */
  async function registerSupportUserRoutes() {
    if (!userContext.isSupportUser.value) {
      return
    }
    registerSupportAssignedRoute(router)
    // The initial navigation resolved before the route existed, so a
    // deep link straight to the assigned view lands on a catch-all
    // (the non-repeatable 'bad-not-found' outranks 'not-found' for
    // typed URLs). If the current URL resolves differently now that
    // the route is registered, re-navigate to pick it up.
    const current = router.currentRoute.value
    if (router.resolve(current.fullPath).name !== current.name) {
      await router.replace(current.fullPath)
    }
  }

  /** [§3 rows 20–23] — SignalR connect plus the runtime subscriptions. */
  async function connectAndSubscribeSignalR(tenantId: string) {
    // [§3 row 20]
    await signalRContext.connectToSignalR({
      tenantId,
      supportSessionToken:
        supportSessionContext.supportSessionToken.value ?? undefined
    })

    // [§3 row 21] — two Diagnostic subscribes (unchanged).
    signalRContext.subscribe(
      signalREvent.Diagnostic.V1.DatabaseGroupJoined,
      () => {
        // console.log('Recieved')
      }
    )

    signalRContext.subscribe(
      signalREvent.Diagnostic.V1.TenantGroupJoined,
      () => {
        // console.log('Recieved')
      }
    )

    // [§3 row 22] — handler now writes through the database storage
    // primitive directly instead of `userContext._addDatabase`.
    signalRContext.subscribe<DatabaseAccessGrantedSignalRPayload>(
      signalREvent.AccessControl.V1.DatabaseAccessGranted,
      ({ message }) => {
        if (message.userId === userContext.id.value) {
          databaseStorage.addDatabase({
            id: message.databaseId,
            name: message.databaseDisplayName,
            tenantId: message.tenantId,
            tenantName: userContext.userProfile.value?.homeTenantName ?? '',
            isDefault: false,
            affectedBySupportSession: false,
            isSelected: false
          })
        }
      }
    )

    // [§3 row 23] — handler refetches via `_fetchGrantedDatabases`,
    // applies the same non-empty storage-write guard as row 10, then
    // uses `databaseStorage.databaseInfo.value.all` for reload/switch.
    signalRContext.subscribe<DatabaseAccessRevokedSignalRPayload>(
      signalREvent.AccessControl.V1.DatabaseAccessRevoked,
      async ({ message }) => {
        const currentDatabaseId = databaseStorage.databaseInfo.value.current?.id
        if (message.userId === userContext.id.value) {
          try {
            const result = await userContext._fetchGrantedDatabases()
            if (
              Array.isArray(result.databases) &&
              result.databases.length > 0
            ) {
              databaseStorage.setDatabases(result.databases)
            }
            const databases = databaseStorage.databaseInfo.value.all
            if (currentDatabaseId === message.databaseId) {
              if (databases.length === 0) {
                window.location.reload()
                return
              }
              // Length check above guarantees this exists.
              userContext.switchDatabase(databases[0]!.id)
            }
          } catch {
            window.location.reload()
          }
        }
      }
    )
  }

  /**
   * Sequences the bootstrap promise-chain. Row labels reference the §3
   * baseline in `docs/plans/archived/platform-session-cluster-promotion.md`
   * so per-step ownership remains traceable.
   */
  async function initializeApp() {
    try {
      // [§3 row 0]
      provideContexts()

      // [§3 row 1]
      await router.isReady()
      // [§3 row 2] Skip init on routes that declare it in their meta
      // (sign-in, sign-out, logged-out). `/oauth2-callback` intentionally
      // does NOT set this flag — init must run there so the target URL has
      // a fully-initialized app.
      if (route.meta.skipInitialization === true) {
        return
      }
      // [§3 row 3]
      appStateContext.startupState.setStartupState(StartupState.Initializing)

      // [§3 row 4] `router.isReady()` above gates `route.meta.skipInitialization`;
      // the support-session bootstrap reads from `window.location` directly
      // and no longer depends on the router being ready.
      await supportSessionContext.getSupportSessionIdFromRouteQuery()
      // [§3 row 5]
      const userIsAuthenticated = await isAuthenticated()
      if (!userIsAuthenticated) {
        throw new Error('User authentication failed')
      }

      // [§3 row 6]
      await userContext._loadUserProfile()
      // [§3 row 7]
      let tenantName = userContext.userProfile.value?.homeTenantName ?? ''
      // [§3 row 8]
      await userContext._loadUserPreferences()

      // [Post-§3 addition] — conditional, role-gated routes.
      await registerSupportUserRoutes()

      // [§3 rows 9–11]
      const tenantId = await resolveTenantId()
      // [§3 row 12]
      gsCloudBackend.setActiveTenantId(tenantId)

      // [§3 row 13]
      if (databaseStorage.hasDatabaseAccess.value) {
        await userContext._loadAccessCodes()
      }
      // [§3 row 14]
      const currentDatabase = databaseStorage.databaseInfo.value.current
      if (currentDatabase) {
        tenantName = currentDatabase.tenantName
      }
      // [§3 row 15]
      const userProfile = userContext.userProfile.value
      if (!userProfile) {
        throw new AppError({
          code: ErrorCode.UNEXPECTED,
          message: 'No user profile found'
        })
      }
      // [§3 row 16] — five AccessControl subscribes wrapped on the user
      // surface; the orchestrator passes `signalRContext` through.
      userContext._subscribeToAccessControlEvents(signalRContext)

      // [§3 rows 17–19]
      await initializeFlagsAndSettings({
        tenantId,
        tenantName,
        userEmail: userProfile.email,
        currentDatabase
      })
      // [§3 rows 20–23]
      await connectAndSubscribeSignalR(tenantId)

      // [§3 row 24]
      appStateContext.startupState.setStartupState(StartupState.ReadyForStartup)
    } catch (error: unknown) {
      const appError = mapToAppError(error)
      appStateContext.startupState.setStartupState(StartupState.Error)
      appStateContext.startupState.setStartupStateError(appError)
      // Re-throw the mapped error so the global handler and sink see a
      // typed `AppError` instead of whatever raw shape the failure had.
      throw appError
    }
  }

  return {
    initializeApp
  }
}
